import { Component, EventEmitter, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location, CommonModule } from '@angular/common';
import moment from 'moment';
import { ButtonComponent } from '../../../@core/components/ui/button/button.component';
import { ToastService } from '../../../@core/services/toast.service';
import { MicrosoftAzureService } from '../../services/microsoft-azure.service';
import { AssetUrlPipe } from 'src/app/@shared/pipes/asset-url.pipe';

/**
 * Microsoft Azure SSO Integration Dialog Component
 * Handles the OAuth consent flow for Microsoft Azure Active Directory
 */
@Component({
    selector: 'app-microsoft-azure',
    standalone: true,
    imports: [ButtonComponent, CommonModule, AssetUrlPipe],
    templateUrl: './microsoft-azure.component.html',
    styleUrl: './microsoft-azure.component.scss'
})
export class MicrosoftAzureComponent {
    @Output() close = new EventEmitter<{ statusChanged?: boolean, connected?: boolean }>();

    /**
     * Whether consent has been granted
     */
    consentExists: boolean = false;

    /**
     * Loading state for connect button
     */
    isConnecting: boolean = false;

    /**
     * Loading state for fetching consent status
     */
    isLoadingConsentStatus: boolean = true;

    /**
     * Dots array for visual connector animation
     */
    dots = [].constructor(6);

    /**
     * Last sync timestamp
     */
    lastSynced: string = '';

    /**
     * Azure AD users count (if consent exists)
     */
    usersCount: number = 0;

    constructor(
        private microsoftAzureService: MicrosoftAzureService,
        private activatedRoute: ActivatedRoute,
        private toast: ToastService,
        private router: Router,
        private location: Location
    ) { }

    ngOnInit() {
        // Check if we're returning from OAuth callback
        const code = this.activatedRoute.snapshot.queryParamMap.get('code');
        if (code) {
            this.handleOAuthCallback(code);
        }

        // Check existing consent status
        this.checkConsentStatus();
    }

    /**
     * Check if Microsoft Azure consent already exists
     * and fetch user count if available
     */
    checkConsentStatus() {
        this.isLoadingConsentStatus = true;
        this.microsoftAzureService.getAzureUsers().subscribe({
            next: (res) => {
                if (res.success && res.data) {
                    this.consentExists = !res.data.disconnected;
                    this.usersCount = res.data.totalCount;
                    this.lastSynced = moment().format('lll');
                }
                this.isLoadingConsentStatus = false;
            },
            error: () => {
                this.consentExists = false;
                this.isLoadingConsentStatus = false;
            }
        });
    }

    /**
     * Handle OAuth callback after user grants consent
     * @param code Authorization code from Microsoft
     */
    handleOAuthCallback(code: string) {
        // Store success status in session storage
        sessionStorage.setItem('microsoftConsentStatus', 'success');
        this.consentExists = true;
        this.toast.success('Microsoft Azure consent granted successfully!');
        this.location.replaceState('/settings/integrations');
        this.checkConsentStatus();
    }

    /**
     * Initiate Microsoft OAuth consent flow for organization-level Azure AD access
     * Opens popup window for user to grant permissions (PKCE flow)
     * Uses a separate callback URL from simple auth to avoid conflicts
     */
    async connectMicrosoft() {
        this.isConnecting = true;

        try {
            // Use organization-specific callback URL (different from simple auth)
            const frontendCallbackUrl = `${window.location.origin}/oauth/microsoft/organization/callback`;

            // Get the consent URL from backend, passing our callback URL
            const response = await this.microsoftAzureService.getConsentUrl(frontendCallbackUrl).toPromise();

            if (response?.success && response.data?.consentUrl) {
                // Open consent popup
                await this.microsoftAzureService.openConsentPopup(response.data.consentUrl);

                // Consent granted successfully
                this.consentExists = true;
                this.toast.success('Microsoft Azure connected successfully!');
                this.checkConsentStatus();

                // Notify parent component and close dialog
                this.close.emit({ statusChanged: true, connected: true });
            } else {
                throw new Error('Failed to get consent URL');
            }
        } catch (error: any) {
            console.error('Microsoft consent error:', error);
            this.toast.error(error.message || 'Failed to connect Microsoft Azure. Please try again.');
        } finally {
            this.isConnecting = false;
        }
    }

    /**
     * Disconnect Microsoft Azure integration
     * Revokes consent and clears stored tokens
     */
    disconnectMicrosoft() {
        // Show confirmation before disconnecting
        if (!confirm('Are you sure you want to disconnect Microsoft Azure? This will revoke access to Azure AD users.')) {
            return;
        }

        this.microsoftAzureService.disconnectAzure().subscribe({
            next: (response) => {
                if (response.success) {
                    // Update local state
                    this.consentExists = false;
                    this.usersCount = 0;
                    this.lastSynced = '';

                    // Show success message
                    this.toast.success('Microsoft Azure disconnected successfully!');

                    // Notify parent component about status change
                    this.close.emit({ statusChanged: true, connected: false });
                } else {
                    this.toast.error(response.message || 'Failed to disconnect Microsoft Azure');
                }
            },
            error: (error) => {
                console.error('Disconnect error details:', error);
                console.error('Error status:', error.status);
                console.error('Error message:', error.error?.message || error.message);

                // Show more specific error message
                const errorMsg = error.error?.message || error.message || 'Failed to disconnect Microsoft Azure. Please try again.';
                this.toast.error(errorMsg);
            }
        });
    }

    /**
     * Sync Azure AD users manually
     */
    syncUsers() {
        this.microsoftAzureService.getAzureUsers().subscribe({
            next: (res) => {
                if (res.success) {
                    this.usersCount = res.data?.totalCount || 0;
                    this.lastSynced = moment().format('lll');
                    this.toast.success(`Synced ${this.usersCount} Azure AD users`);
                }
            },
            error: (error) => {
                this.toast.error('Failed to sync users');
            }
        });
    }
}

